import { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  TableTemplate,
  TableControls,
  Selector,
  Placeholder,
  TablePagination,
  Tag,
  QubeIcons,
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
} from 'qw-ui-lib-v2';
import I18n from '../../locale';
import './group_styles.scss';

export default function TheatreGroupDetailsTable({ data, isLoading, isError }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [locationQuery, setLocationQuery] = useState('');

  const columns = useMemo(
    () => [
      {
        header: I18n.t('theatreSmall'),
        id: 'name',
        accessorKey: 'name',
        filter: 'text',
      },
      {
        header: I18n.t('chainSmall'),
        id: 'chain',
        accessorKey: 'chain.display_name',
      },
      {
        header: I18n.t('location'),
        id: 'location',
        accessorFn: (value) =>
          [value?.address?.city, value?.address?.state, value?.address?.country]
            .filter(Boolean)
            .join(', '),
        filter: 'includesSome',
      },
      {
        header: I18n.t('screens'),
        id: 'screens',
        accessorKey: 'screen_count',
      },
    ],
    [],
  );

  const tableColumns = useMemo(
    () =>
      isLoading
        ? columns.map((column) => ({
            ...column,
            cell: () => <Placeholder type="textRow" />,
          }))
        : columns,
    [isLoading, columns],
  );

  const tableData = useMemo(
    () => (isLoading ? Array(10).fill({}) : (data ?? [])),
    [isLoading, data],
  );

  const {
    getHeaderGroups,
    getRowModel,
    setPageIndex: gotoPage,
    setPageSize,
    getRowCount,
    setColumnFilters,
    getState,
  } = useReactTable({
    columns: tableColumns,
    data: tableData,
    defaultColumn: { size: 120, minSize: 48, maxSize: 400 },
    initialState: {
      pagination: { pageSize: 15 },
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });
  const {
    pagination: { pageIndex, pageSize },
    columnFilters: filters,
  } = getState();

  const setFilter = useCallback(
    (columnId, value) => {
      setColumnFilters((old) => {
        const others = old.filter((f) => f.id !== columnId);
        if (!value || (Array.isArray(value) && value.length === 0)) {
          return others;
        }
        return [...others, { id: columnId, value }];
      });
    },
    [setColumnFilters],
  );

  const locationFilterValues = useMemo(() => {
    return filters
      .filter((f) => f.id === 'location')
      .map((f) => f.value)
      .filter(Boolean)
      .flat();
  }, [filters]);

  const locationsList = useMemo(() => {
    const suggestions = data?.reduce((acc, group) => {
      const { address } = group;
      if (address) {
        const { city, state, country } = address;
        if (city) acc.add(`${city}, ${state}, ${country}`);
        if (state) acc.add(`${state}, ${country}`);
        if (country) acc.add(country);
      }
      return acc;
    }, new Set());
    return Array.from(suggestions);
  }, [data]);

  const locationSuggestions = useMemo(() => {
    return locationsList
      .filter(
        (item) =>
          locationQuery &&
          item.toLowerCase().includes(locationQuery.toLowerCase()),
      )
      .sort((a, b) => {
        let isGoodMatchA = a.toLowerCase().indexOf(locationQuery.toLowerCase());
        let isGoodMatchB = b.toLowerCase().indexOf(locationQuery.toLowerCase());
        isGoodMatchA = isGoodMatchA === -1 ? 999 : isGoodMatchA;
        isGoodMatchB = isGoodMatchB === -1 ? 999 : isGoodMatchB;
        if (isGoodMatchA > isGoodMatchB) return 1;
        if (isGoodMatchA < isGoodMatchB) return -1;
        if (a > b) return 1;
        return -1;
      });
  }, [locationQuery, locationsList]);

  const controls = useMemo(
    () => [
      {
        id: 'locationFilter',
        component: () => (
          <Selector
            onSelect={(selected) => {
              setFilter('location', selected);
              gotoPage(0);
            }}
            searchProps={{
              placeholder: I18n.t('searchByLocation'),
              value: locationQuery,
              onChange: (e) => setLocationQuery(e.target.value),
              onSearchClear: () => setLocationQuery(''),
            }}
            renderMenuContent={
              locationQuery && locationSuggestions?.length === 0 ? (
                <div className="p1 center">{I18n.t('noResultsFound')}</div>
              ) : null
            }
            list={locationSuggestions}
            selectedList={locationFilterValues || []}
            placeHolder={I18n.t('location')}
            buttonType="default"
            overridePlaceholder
            showLabelInButton={false}
            noSelectedIcon={false}
            defaultIsOpen={false}
            usePortal={false}
            closeOnSelect={false}
            enableCheckbox
            clearSelectionText={I18n.t('clearSelection')}
          />
        ),
      },
    ],
    [
      gotoPage,
      locationFilterValues,
      locationQuery,
      locationSuggestions,
      setFilter,
    ],
  );

  const handleSearch = (query) => {
    setSearchQuery(query);
    setFilter('name', query);
    gotoPage(0);
  };

  return (
    <div className="fixed-table">
      <div className="table-header">{I18n.t('theatresList')}</div>
      <TableControls
        searchBar
        inputWithSelection
        searchFilterIsStatic
        searchType="inputSearch"
        searchInputProps={{
          placeholder: I18n.t('searchByTheatreName'),
          inputValue: searchQuery,
          onChange: (e) => handleSearch(e.target.value),
        }}
        onSearchClear={() => handleSearch('')}
        totalResults={data?.length}
        controls={controls}
      />
      {locationFilterValues.length > 0 && (
        <div className="table-filter">
          {I18n.t('showingResultsFor')}
          {locationFilterValues.map((location) => (
            <Tag
              className="qw-regular"
              key={location}
              closeIcon={<QubeIcons.CancelRoundedIcon color="#000" />}
              onClose={() =>
                setFilter(
                  'location',
                  locationFilterValues.filter((v) => v !== location),
                )
              }
            >
              {`${I18n.t('location')}: `}
              <strong>{location}</strong>
            </Tag>
          ))}
          <Tag
            color="secondary"
            className="qw-cursor-pointer"
            onClick={() => setFilter('location', [])}
          >
            {I18n.t('clearAll')}
          </Tag>
        </div>
      )}
      <TableTemplate
        sortable
        rowSeparator
        getHeaderGroups={getHeaderGroups}
        getRowModel={getRowModel}
      />
      {getRowCount() === 0 && !isLoading && !isError && (
        <div className="flex align-center qw-justify-center qw-p-medium">
          {I18n.t('noResultsFound')}
        </div>
      )}
      {!getRowCount() && isError && !isLoading && (
        <div className="flex align-center qw-justify-center qw-p-medium">
          {I18n.t('somethingWentWrong')}
        </div>
      )}
      <div className="table-footer">
        <div className="table-page-count">
          {I18n.t('showingResults', {
            start: getRowCount() === 0 ? 0 : pageIndex * pageSize + 1,
            end: Math.min((pageIndex + 1) * pageSize, getRowCount()),
            total: getRowCount(),
          })}
        </div>
        <div className="table-page-selector">
          <div className="page-selector">
            {I18n.t('show')}
            <Selector
              onSelect={([size]) => {
                setPageSize(size);
                gotoPage(0);
              }}
              selectedList={[pageSize]}
              list={[15, 30, 45]}
              showLabelInButton={false}
              singleSelect
              closeOnSelect
              noSelectedIcon={false}
              defaultIsOpen={false}
              usePortal={false}
              placeHolder="-"
            />
          </div>
          <TablePagination
            pageSize={pageSize}
            currentPage={pageIndex + 1}
            onPageChange={(nextPage) => gotoPage(nextPage - 1)}
            totalRecords={getRowCount()}
          />
        </div>
      </div>
    </div>
  );
}

TheatreGroupDetailsTable.propTypes = {
  data: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
};
